import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    quote: "We were honestly confused where to start with interiors, but the team made everything so easy. They understood exactly what we wanted and the house turned out even better than we imagined.",
    name: "Ananya Sharma",
    location: "Bangalore",
  },
  {
    id: 2,
    quote: "Really happy with how the whole process went. No unnecessary complications, clear communication, and the final result looks clean and premium.",
    name: "Rahul Verma",
    location: "Bangalore",
  },
  {
    id: 3,
    quote: "They didn't just design, they actually listened. Small details that we mentioned casually were taken care of, and that made a big difference.",
    name: "Sneha Iyer",
    location: "Bangalore",
  },
  {
    id: 4,
    quote: "Work was completed on time, and the quality is solid. What I liked most is that nothing feels overdone—it's simple and elegant.",
    name: "Anuj Mehta",
    location: "Bangalore",
  },
  {
    id: 5,
    quote: "We get compliments every time someone visits our home. It feels comfortable, practical, and looks great without being too flashy.",
    name: "Poojashree Nair",
    location: "Bangalore",
  },
];

export const TestimonialSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section
      ref={ref}
      data-testid="testimonial-section"
      className="section-padding bg-[#F5F0E8]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="overline text-[#B5703A] block mb-4">Testimonials</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2A2A2A] tracking-tight">
            What Our Clients Say
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          {/* Main Testimonial Card */}
          <div className="bg-[#D9CFC0] p-8 lg:p-12 text-center relative">
            {/* Quote mark */}
            <Quote size={48} className="text-[#C4B6A4] mx-auto mb-6" />

            {/* Quote */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <blockquote className="text-xl sm:text-2xl lg:text-3xl font-light text-[#2A2A2A] leading-relaxed tracking-tight mb-8 max-w-3xl mx-auto">
                  "{currentTestimonial.quote}"
                </blockquote>

                {/* Attribution */}
                <div className="flex items-center justify-center gap-2">
                  <div className="text-center">
                    <span className="block text-[#2A2A2A] font-medium">— {currentTestimonial.name}</span>
                    <span className="block text-sm text-[#3D3D3D]">{currentTestimonial.location}</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#F5F0E8] border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#F5F0E8] border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
              data-testid="testimonial-next"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-[#B5703A] w-6' : 'bg-[#C4B6A4]'
                }`}
                data-testid={`testimonial-dot-${index}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialSection;
